import { defineStore, storeToRefs } from 'pinia'
import { computed, ref, watch } from 'vue'
import {
  arrayRemove,
  arrayUnion,
  collection,
  deleteDoc,
  doc,
  documentId,
  getDoc,
  getDocs,
  limit,
  onSnapshot,
  query,
  serverTimestamp,
  setDoc,
  startAfter,
  updateDoc,
  where,
  writeBatch,
  type DocumentData,
  type DocumentReference,
  type Unsubscribe
} from 'firebase/firestore'
import { useCollection } from 'vuefire'
import { ref as storageRef, uploadBytes, getDownloadURL, getBytes, deleteObject } from 'firebase/storage'
import { getFunctions, httpsCallable } from 'firebase/functions'
import { firestore, storage } from '@/fb'
import { debug, warn } from '@/logger'
import type { Recipe, RecipeBook, RecipeBookMember, RecipeIngredient, ShoppingListItem, UserProfile } from '@/models/firestore'
import { recipeBookConverter, recipeConverter } from '@/models/firestoreConverters'
import { useUserStore } from './userStore'
import { useAiJobStore } from './aiJobStore'
import { getAllRecipeIngredients } from '@/utils/recipeIngredients'

export interface ScannedRecipe {
  name?: string
  description?: string
  servings?: string
  prepTime?: string
  cookTime?: string
  directions?: Array<{
    text: string
    ingredients?: Array<{ name: string; quantity?: string | number; measurement?: string }>
  }>
  tags?: string[]
}

// Legacy data structure for backward compatibility with data.ts facade
export interface RecipeBooks {
  docs: string[]
  [key: string]: RecipeBook | string[]
}

interface SaveRecipePayload {
  recipeId?: string
  ownerId?: string | null
  name: string
  description?: string
  discoverable: boolean
  ingredients?: RecipeIngredient[]  // Optional - for backward compatibility only
  directions: Array<{ text: string; imageUrl?: string; imageRef?: string; ingredients?: RecipeIngredient[] }>
  tags: Array<{ name: string }>
  recipeBooks: Array<{ id: string }>
  previousRecipeBooks: Array<{ id: string }>
  imageFile?: File | null
  imageUrl?: string  // For AI-generated images
  imageRef?: string // Storage ref for server-generated images
  // Recipe metadata
  servings?: number | null
  prepTime?: string
  cookTime?: string
  difficulty?: string | null
}

export const useRecipeStore = defineStore('recipe', () => {
  const userStore = useUserStore()
  const { user } = storeToRefs(userStore)

  // ==========================================
  // VueFire Reactive Collection Binding (Lazy)
  // ==========================================

  // Computed query source - returns null when no recipe books to query (lazy loading)
  const recipeBooksQuerySource = computed(() => {
    const bookIds = user.value?.recipe_books
    if (!bookIds || bookIds.length === 0) return null

    // Firestore 'in' queries limited to 30 items
    return query(
      collection(firestore, 'recipe_books'),
      where(documentId(), 'in', bookIds.slice(0, 30))
    )
  })

  // VueFire reactive binding for recipe books
  const {
    data: recipeBooksData,
    pending: recipeBooksLoading,
    error: recipeBooksError
  } = useCollection<RecipeBook>(recipeBooksQuerySource, { ssrKey: 'recipeBooks' })

  const unfiledRecipesQuerySource = computed(() => {
    const uid = user.value?.uid
    if (!uid) return null
    return query(
      collection(firestore, 'recipes').withConverter(recipeConverter),
      where('owner', '==', uid),
      where('orphaned_at', '!=', null)
    )
  })
  const { data: unfiledRecipesData } = useCollection<Recipe>(
    unfiledRecipesQuerySource,
    { ssrKey: 'unfiledRecipes' }
  )
  const unfiledRecipes = computed<Recipe[]>(() =>
    (unfiledRecipesData.value || []).map(recipe => ({ ...recipe, id: recipe.id }))
  )

  // Extended recipe book data with members and recipes (populated by subcollection listeners)
  const recipeBookExtensions = ref<Map<string, { members: RecipeBookMember[], recipes: Recipe[] }>>(new Map())

  // Merge VueFire data with extensions
  const recipeBooksArray = computed<RecipeBook[]>(() => {
    if (!recipeBooksData.value) return []
    return recipeBooksData.value.map(b => {
      const ext = recipeBookExtensions.value.get(b.id!)
      return {
        ...b,
        id: b.id,
        members: ext?.members ?? b.members ?? [],
        recipes: ext?.recipes ?? []
      }
    })
  })

  // Other state
  const searchQuery = ref<string | null>(null)
  const currentRecipe = ref<Recipe | null>(null)
  const discoveredRecipes = ref<Record<string, Recipe>>({})
  const lastDiscoveredRecipe = ref<any>(null)
  const moreToDiscover = ref(true)

  // Listener tracking for subcollections and current recipe
  const currentRecipeUnsubscribe = ref<Unsubscribe | null>(null)
  const recipeBookMembersUnsubscribes = ref<Map<string, Unsubscribe>>(new Map())
  const recipeBookRecipesUnsubscribes = ref<Map<string, Unsubscribe>>(new Map())

  // ==========================================
  // Subcollection Binding (triggered by recipe book list changes)
  // ==========================================

  // Watch for recipe book list changes and bind subcollections
  watch(
    recipeBooksData,
    (newBooks) => {
      if (!newBooks) return

      const currentIds = new Set(newBooks.map(b => b.id!))

      // Subscribe to subcollections for new recipe books
      for (const book of newBooks) {
        if (book.id && !recipeBookMembersUnsubscribes.value.has(book.id)) {
          bindRecipeBookMembers(book.id)
          bindRecipeBookRecipes(book.id)
        }
      }

      // Unsubscribe from removed recipe books
      for (const [id, unsub] of recipeBookMembersUnsubscribes.value) {
        if (!currentIds.has(id)) {
          unsub()
          recipeBookMembersUnsubscribes.value.delete(id)
        }
      }
      for (const [id, unsub] of recipeBookRecipesUnsubscribes.value) {
        if (!currentIds.has(id)) {
          unsub()
          recipeBookRecipesUnsubscribes.value.delete(id)
        }
      }
    },
    { immediate: true }
  )

  // Helper functions
  const findRecipeBook = (id: string): RecipeBook | undefined => {
    return recipeBooksArray.value.find(b => b.id === id)
  }

  const findRecipeBookIndex = (id: string): number => {
    return recipeBooksArray.value.findIndex(b => b.id === id)
  }
  const resizeImageToJpegCover = async (
    file: File,
    options?: { width?: number; height?: number; quality?: number }
  ): Promise<Blob> => {
    const width = options?.width ?? 1280
    const height = options?.height ?? 720
    const quality = options?.quality ?? 0.82

    const objectUrl = URL.createObjectURL(file)

    const loadImage = async (): Promise<HTMLImageElement> => {
      return await new Promise((resolve, reject) => {
        const img = new Image()
        img.onload = () => resolve(img)
        img.onerror = () => reject(new Error('Failed to load image'))
        img.src = objectUrl
      })
    }

    try {
      const canvas = document.createElement('canvas')
      canvas.width = width
      canvas.height = height

      const ctx = canvas.getContext('2d')
      if (!ctx) throw new Error('Canvas 2D context not available')

      let sourceWidth: number
      let sourceHeight: number
      let drawSource: CanvasImageSource

      try {
        const bitmap = await createImageBitmap(file)
        sourceWidth = bitmap.width
        sourceHeight = bitmap.height
        drawSource = bitmap
      } catch {
        const img = await loadImage()
        sourceWidth = img.naturalWidth || img.width
        sourceHeight = img.naturalHeight || img.height
        drawSource = img
      }

      const targetRatio = width / height
      const srcRatio = sourceWidth / sourceHeight

      let sx = 0
      let sy = 0
      let sw = sourceWidth
      let sh = sourceHeight

      if (srcRatio > targetRatio) {
        sw = Math.round(sourceHeight * targetRatio)
        sx = Math.round((sourceWidth - sw) / 2)
      } else if (srcRatio < targetRatio) {
        sh = Math.round(sourceWidth / targetRatio)
        sy = Math.round((sourceHeight - sh) / 2)
      }

      ctx.drawImage(drawSource, sx, sy, sw, sh, 0, 0, width, height)

      const blob = await new Promise<Blob>((resolve, reject) => {
        canvas.toBlob(
          (b) => (b ? resolve(b) : reject(new Error('Failed to encode image'))),
          'image/jpeg',
          quality
        )
      })

      return blob
    } finally {
      URL.revokeObjectURL(objectUrl)
    }
  }

  // Legacy getter for backward compatibility with data.ts facade
  const recipeBooks = computed((): RecipeBooks => {
    const result: RecipeBooks = { docs: [] }
    for (const book of recipeBooksArray.value) {
      if (book.id) {
        result.docs.push(book.id)
        result[book.id] = book
      }
    }
    return result
  })

  // Simple array getters
  const getRecipeBooks = computed(() => recipeBooksArray.value)

  const getRecipeBooksDocs = computed((): string[] => {
    return recipeBooksArray.value.map(b => b.id!).filter(Boolean)
  })
  const getSearchQuery = computed((): string | null => searchQuery.value)
  const getCurrentRecipe = computed((): Recipe | null => currentRecipe.value)
  const getDiscoveredRecipes = computed((): Record<string, Recipe> => discoveredRecipes.value)
  const getLastDiscoveredRecipe = computed((): any => lastDiscoveredRecipe.value)
  const getMoreToDiscover = computed((): boolean => moreToDiscover.value)

  const getRecipeBookForAccess = (id: string): RecipeBook | null => {
    return findRecipeBook(id) ?? null
  }

  // Get the mutable extension object for this recipe book (for subcollection listeners)
  const getRecipeBookExtension = (id: string): { members: RecipeBookMember[], recipes: Recipe[] } => {
    if (!recipeBookExtensions.value.has(id)) {
      recipeBookExtensions.value.set(id, { members: [], recipes: [] })
    }
    return recipeBookExtensions.value.get(id)!
  }

  const recipeBooksContainingRecipe = (recipeId: string): RecipeBook[] => {
    return recipeBooksArray.value.filter(book =>
      book.recipes?.some(recipe => recipe.id === recipeId)
    )
  }
  const recipeFromRecipeBook = ({ recipeBookId, recipeId }: { recipeBookId: string; recipeId: string }): Recipe | {} => {
    const book = getRecipeBookForAccess(recipeBookId)
    if (!book?.recipes) return {}
    return book.recipes.find(r => r.id === recipeId) ?? {}
  }

  const recipeBook = (id: string): RecipeBook | {} => getRecipeBookForAccess(id) ?? {}

  const recipeBookMembers = (id: string): RecipeBookMember[] => getRecipeBookForAccess(id)?.members ?? []

  const recipeBookRecipesFiltered = (id: string): Recipe[] => {
    const book = getRecipeBookForAccess(id)
    if (!book?.recipes) return []
    const queryValue = searchQuery.value

    if (!queryValue) return book.recipes

    return book.recipes.filter((recipe: Recipe) => {
      return (
        recipe.name?.toLowerCase().includes(queryValue.toLowerCase()) ||
        getAllRecipeIngredients(recipe).some((ingredient) => ingredient.name?.toLowerCase().includes(queryValue.toLowerCase()))
      )
    })
  }

  const userAddedShoppingListItems = computed((): ShoppingListItem[] => [])
  const loading = computed((): boolean => false)
  const getIngredientsSmartSuggestions = () => []

  // Get all recipes across all recipe books
  const getAllRecipes = computed((): Recipe[] => {
    const allRecipes: Recipe[] = []
    const seenIds = new Set<string>()
    for (const book of recipeBooksArray.value) {
      for (const recipe of book.recipes ?? []) {
        if (recipe.id && !seenIds.has(recipe.id)) {
          seenIds.add(recipe.id)
          allRecipes.push(recipe)
        }
      }
    }
    return allRecipes
  })

  // Get recipes by IDs (for favorites)
  const getRecipesByIds = (ids: string[]): Recipe[] => {
    return getAllRecipes.value.filter(r => r.id && ids.includes(r.id))
  }

  // Unbind helpers for subcollections
  const unbindRecipeBookMembersListener = (id: string) => {
    const unsub = recipeBookMembersUnsubscribes.value.get(id)
    if (unsub) {
      unsub()
      recipeBookMembersUnsubscribes.value.delete(id)
    }
  }

  const unbindRecipeBookRecipesListener = (id: string) => {
    const unsub = recipeBookRecipesUnsubscribes.value.get(id)
    if (unsub) {
      unsub()
      recipeBookRecipesUnsubscribes.value.delete(id)
    }
  }

  const unbindAllSubcollections = () => {
    recipeBookMembersUnsubscribes.value.forEach((unsub) => unsub())
    recipeBookMembersUnsubscribes.value.clear()
    recipeBookRecipesUnsubscribes.value.forEach((unsub) => unsub())
    recipeBookRecipesUnsubscribes.value.clear()
    recipeBookExtensions.value.clear()
  }

  const unbindCurrentRecipe = () => {
    if (currentRecipeUnsubscribe.value) {
      currentRecipeUnsubscribe.value()
      currentRecipeUnsubscribe.value = null
    }
  }

  // Actions
  const setSearchQuery = (queryStr: string | null) => {
    searchQuery.value = queryStr ? queryStr.toLowerCase() : null
  }

  const setCurrentRecipe = (recipe: Recipe | null) => {
    currentRecipe.value = recipe
  }

  const clearDiscoveredRecipes = () => {
    discoveredRecipes.value = {}
    moreToDiscover.value = true
    lastDiscoveredRecipe.value = null
  }

  const addDiscoveredRecipe = ({ id, discoveredRecipe }: { id: string; discoveredRecipe: Recipe }) => {
    discoveredRecipes.value[id] = discoveredRecipe
  }

  const setLastDiscoveredRecipe = (recipe: any) => {
    lastDiscoveredRecipe.value = recipe
  }

  const setMoreToDiscover = (more: boolean) => {
    moreToDiscover.value = more
  }

  const bindCurrentRecipe = async (recipeId: string) => {
    unbindCurrentRecipe()

    if (!recipeId) return

    try {
      const recipeRef = doc(collection(firestore, 'recipes').withConverter(recipeConverter), recipeId)

      currentRecipeUnsubscribe.value = onSnapshot(recipeRef, (snapshot) => {
        if (snapshot.exists()) {
          setCurrentRecipe({ id: snapshot.id, ...snapshot.data() })
        } else {
          setCurrentRecipe(null)
        }
      }, (error) => {
        warn('bindCurrentRecipe: Error in snapshot listener: ' + error.message)
      })
    } catch (error) {
      warn('bindCurrentRecipe: Error setting up listener: ' + (error as Error).message)
    }
  }

  const bindRecipeBookMembers = async (id: string) => {
    if (!id) return

    if (recipeBookMembersUnsubscribes.value.has(id)) {
      debug('bindRecipeBookMembers: Already bound for book ' + id)
      return
    }

    try {
      const membersRef = collection(firestore, 'recipe_books', id, 'members')

      const unsub = onSnapshot(membersRef, async (snapshot) => {
        const members: RecipeBookMember[] = []

        for (const memberDoc of snapshot.docs) {
          const memberData = memberDoc.data() as any
          if (memberData.deleted) continue

          // Resolve the actual user for this membership.
          // In the common case, memberDoc.id is the user's UID.
          // Some legacy/alternate shapes may store an explicit userId or a DocumentReference.
          const resolvedUserId: string =
            (typeof memberData?.userId === 'string' && memberData.userId) ||
            (typeof memberData?.uid === 'string' && memberData.uid) ||
            (typeof memberData?.ref?.id === 'string' && memberData.ref.id) ||
            memberDoc.id

          const resolvedUserPath = resolvedUserId
            ? `public_profiles/${resolvedUserId}`
            : null

          // Try to fetch user profile from Firestore
          if (resolvedUserPath) {
            try {
              const userDocRef = doc(firestore, resolvedUserPath)
              const userDocSnapshot = await getDoc(userDocRef)
              if (userDocSnapshot.exists()) {
                const profile = userDocSnapshot.data() as Record<string, any>
                // Public profiles intentionally omit email and entitlement data.
                let displayName = profile?.name || profile?.displayName

                const resolvedImageUrl: string | null =
                  profile?.image_url ||
                  profile?.photoURL ||
                  profile?.photoUrl ||
                  profile?.imageUrl ||
                  memberData?.image_url ||
                  null

                members.push({
                  id: resolvedUserId,
                  name: displayName || 'Unknown User',
                  image_url: resolvedImageUrl,
                  email: memberData?.email || null,
                  role: memberData?.role ?? 'member'
                })
                continue
              }
            } catch (e) {
              // Fall through to member data fallback
            }
          }

          // Fallback to member document data
          let fallbackName = memberData?.name || memberData?.email?.split('@')[0]
          members.push({
            id: resolvedUserId,
            name: fallbackName || 'Unknown User',
            image_url: memberData?.image_url || null,
            email: memberData?.email || null,
            role: memberData?.role ?? 'member'
          })
        }

        const ext = getRecipeBookExtension(id)
        ext.members = members
      }, (error) => {
        warn('bindRecipeBookMembers: Error in snapshot: ' + error.message)
        const ext = getRecipeBookExtension(id)
        ext.members = []
      })

      recipeBookMembersUnsubscribes.value.set(id, unsub)
    } catch (error) {
      warn('bindRecipeBookMembers: Error setting up listener: ' + (error as Error).message)
      const ext = getRecipeBookExtension(id)
      ext.members = []
    }
  }

  const bindRecipeBookRecipes = async (id: string) => {
    if (!id) return

    if (recipeBookRecipesUnsubscribes.value.has(id)) {
      debug('bindRecipeBookRecipes: Already bound for book ' + id)
      return
    }

    try {
      const recipePagesRef = collection(firestore, 'recipe_books', id, 'recipe_pages')

      const unsub = onSnapshot(recipePagesRef, async (snapshot) => {
        const recipes: Recipe[] = []

        for (const recipePage of snapshot.docs) {
          const pageData = recipePage.data() as any
          if (pageData.deleted) continue

          let recipeData: Recipe | null = null

          if (pageData.ref?.path) {
            try {
              const recipeRef = doc(firestore, pageData.ref.path).withConverter(recipeConverter)
              const recipeSnapshot = await getDoc(recipeRef)
              if (recipeSnapshot.exists()) {
                recipeData = {
                  id: recipeSnapshot.id,
                  ...recipeSnapshot.data()
                }
              }
            } catch (e) {
              // Fall through
            }
          }

          if (!recipeData && !pageData.ref) {
            recipeData = {
              id: recipePage.id,
              ...pageData
            }
          }

          if (recipeData) {
            recipes.push(recipeData)
          }
        }

        const ext = getRecipeBookExtension(id)
        ext.recipes = recipes
      }, (error) => {
        warn('bindRecipeBookRecipes: Error in snapshot: ' + error.message)
      })

      recipeBookRecipesUnsubscribes.value.set(id, unsub)
    } catch (error) {
      warn('bindRecipeBookRecipes: Error setting up listener: ' + (error as Error).message)
    }
  }

  const syncRecipeBookReaderUids = async (recipeBookId: string, recipeId: string): Promise<void> => {
    try {
      const syncReaders = httpsCallable(getFunctions(), 'syncRecipeBookReaderUids')
      await syncReaders({ recipeBookId, recipeId })
    } catch (error) {
      warn('syncRecipeBookReaderUids: Best-effort reader sync failed', error)
    }
  }

  const bindAllRecipeBookRecipes = async () => {
    for (const book of recipeBooksArray.value) {
      if (book.id) await bindRecipeBookRecipes(book.id)
    }
  }

  const addUserToRecipeBook = async ({ userId, recipeBookId }: { userId: string; recipeBookId: string }) => {
    if (!userId || !recipeBookId) return
    try {
      const addMember = httpsCallable<
        { userId: string; recipeBookId: string },
        void
      >(getFunctions(), 'addUserToRecipeBook')
      await addMember({ userId, recipeBookId })
    } catch (error) {
      warn('addUserToRecipeBook: Error', error)
      throw error
    }
  }

  const removeUserFromRecipeBook = async ({ userId, recipeBookId }: { userId: string; recipeBookId: string }) => {
    if (!userId || !recipeBookId) return
    try {
      // Check if there are any members left
      const membersSnapshot = await getDocs(collection(firestore, 'recipe_books', recipeBookId, 'members'))
      const remainingMembers = membersSnapshot.docs.filter(
        d => d.id !== userId && !d.data()?.deleted
      )

      if (remainingMembers.length === 0) {
        // Mark pages while this user still has access; recipe metadata is maintained by Functions.
        await deleteRecipeBookAndOrphanedRecipes(recipeBookId)
      }

      await deleteDoc(doc(firestore, 'recipe_books', recipeBookId, 'members', userId))
      await updateDoc(doc(firestore, 'users', userId), {
        recipe_books: arrayRemove(recipeBookId)
      })
    } catch (error) {
      warn('removeUserFromRecipeBook: Error', error)
    }
  }

  const deleteRecipeBookAndOrphanedRecipes = async (recipeBookId: string) => {
    const recipeBookRef = doc(firestore, 'recipe_books', recipeBookId)

    // Get all recipe pages in this book
    const pagesSnapshot = await getDocs(collection(recipeBookRef, 'recipe_pages'))

    for (const pageDoc of pagesSnapshot.docs) {
      if (pageDoc.data()?.deleted) continue

      await setDoc(pageDoc.ref, { deleted: true }, { merge: true })
    }

    // Delete the recipe book itself
    await deleteDoc(recipeBookRef)
  }

  const discoverMoreRecipes = async () => {
    const recipesRef = collection(firestore, 'recipes').withConverter(recipeConverter)
    let q = query(recipesRef, where('discoverable', '==', true))
    if (lastDiscoveredRecipe.value) {
      q = query(q, startAfter(lastDiscoveredRecipe.value))
    }
    q = query(q, limit(10))

    const snapshot = await getDocs(q)
    if (snapshot.empty) {
      setMoreToDiscover(false)
      return
    }

    setLastDiscoveredRecipe(snapshot.docs[snapshot.docs.length - 1])
    snapshot.docs.forEach((docSnap) => {
      addDiscoveredRecipe({ id: docSnap.id, discoveredRecipe: docSnap.data() })
    })
  }

  const fetchPublicDiscoverableRecipes = async ({ limitCount }: { limitCount: number }): Promise<Recipe[]> => {
    const safeLimit = Math.max(1, Math.min(200, Math.floor(limitCount)))
    const recipesRef = collection(firestore, 'recipes').withConverter(recipeConverter)
    const q = query(recipesRef, where('discoverable', '==', true), limit(safeLimit))

    const snap = await getDocs(q)
    return snap.docs.map((d) => ({ ...d.data(), id: d.id }))
  }

  const upsertRecipeBook = async ({ recipeBookId, name }: { recipeBookId?: string; name: string }) => {
    const recipeBooksRef = collection(firestore, 'recipe_books').withConverter(recipeBookConverter)
    const bookRef = recipeBookId ? doc(recipeBooksRef, recipeBookId) : doc(recipeBooksRef)
    await setDoc(bookRef, { name }, { merge: true })
    return bookRef.id
  }

  const updateRecipeBookFields = async (
    recipeBookId: string,
    updates: { name?: string; purpose?: string | null; cover_image_url?: string | null; cover_image_ref?: string | null }
  ): Promise<void> => {
    if (!recipeBookId) return
    const recipeBookRef = doc(firestore, 'recipe_books', recipeBookId)
    const payload: Record<string, unknown> = {}
    if (typeof updates.name === 'string') payload.name = updates.name
    if (updates.purpose !== undefined) payload.purpose = updates.purpose
    if (updates.cover_image_url !== undefined) payload.cover_image_url = updates.cover_image_url
    if (updates.cover_image_ref !== undefined) payload.cover_image_ref = updates.cover_image_ref
    await setDoc(recipeBookRef, payload, { merge: true })
  }

  const applyRecipeBookCoverFromJob = async (recipeBookId: string, imageUrl: string, imageRef?: string | null) => {
    if (!recipeBookId || !imageUrl) return

    // Best-effort cleanup of previous cover to avoid stale storage entries
    try {
      const snap = await getDoc(doc(firestore, 'recipe_books', recipeBookId))
      const previousRef = snap.exists() ? (snap.data() as DocumentData)?.cover_image_ref : null
      if (typeof previousRef === 'string' && previousRef.trim() && previousRef !== imageRef) {
        await deleteObject(storageRef(storage, previousRef.trim()))
      }
    } catch (e) {
      // ignore cleanup failures
    }

    await updateRecipeBookFields(recipeBookId, {
      cover_image_url: imageUrl,
      cover_image_ref: imageRef ?? null
    })
  }

  const upsertRecipeBookWithDetails = async (payload: {
    recipeBookId?: string
    name: string
    purpose?: string | null
    householdId?: string | null
  }): Promise<string> => {
    const recipeBooksRef = collection(firestore, 'recipe_books').withConverter(recipeBookConverter)
    const bookRef = payload.recipeBookId ? doc(recipeBooksRef, payload.recipeBookId) : doc(recipeBooksRef)

    const docData: Record<string, unknown> = { name: payload.name }
    if (payload.purpose !== undefined) {
      docData.purpose = payload.purpose
    }
    if (payload.householdId !== undefined) {
      docData.householdId = payload.householdId
    }

    await setDoc(bookRef, docData, { merge: true })
    return bookRef.id
  }

  /**
   * Ensure every user has a personal recipe book available immediately after signup.
   * The user profile is checked again here because the VueFire profile binding may
   * still be resolving when the auth callback runs.
   */
  const ensurePersonalRecipeBook = async (profile: UserProfile): Promise<string | null> => {
    if (!profile.uid) return null

    try {
      const userRef = doc(firestore, 'users', profile.uid)
      const userSnapshot = await getDoc(userRef)
      const existingRecipeBooks = userSnapshot.exists()
        ? userSnapshot.data()?.recipe_books
        : undefined

      if (
        Array.isArray(existingRecipeBooks) &&
        existingRecipeBooks.some((recipeBookId): recipeBookId is string =>
          typeof recipeBookId === 'string' && recipeBookId.length > 0
        )
      ) {
        return existingRecipeBooks.find(
          (recipeBookId): recipeBookId is string =>
            typeof recipeBookId === 'string' && recipeBookId.length > 0
        ) ?? null
      }

      const recipeBooksRef = collection(firestore, 'recipe_books').withConverter(recipeBookConverter)
      const recipeBookRef = doc(recipeBooksRef)
      const memberRef = doc(firestore, 'recipe_books', recipeBookRef.id, 'members', profile.uid)
      const batch = writeBatch(firestore)

      batch.set(recipeBookRef, {
        name: 'My Recipes',
        purpose: 'Your personal recipe collection',
        recipe_count: 0,
        member_count: 1,
        created_at: serverTimestamp()
      })
      batch.set(memberRef, {
        userId: profile.uid,
        added_at: serverTimestamp(),
        role: 'owner',
        ref: userRef
      })
      batch.set(userRef, {
        recipe_books: arrayUnion(recipeBookRef.id)
      }, { merge: true })

      await batch.commit()
      return recipeBookRef.id
    } catch (error) {
      warn('ensurePersonalRecipeBook: Error creating default recipe book', error)
      return null
    }
  }

  const attachCoverImageToRecipeBook = async (imageFile: File, recipeBookId: string): Promise<{ imageUrl: string; imageRef: string }> => {
    if (!imageFile || !recipeBookId) {
      throw new Error('attachCoverImageToRecipeBook: Missing imageFile or recipeBookId')
    }

    // Best-effort cleanup of previous cover to avoid caching + storage bloat.
    try {
      const snap = await getDoc(doc(firestore, 'recipe_books', recipeBookId))
      const previousRef = snap.exists() ? (snap.data() as DocumentData)?.cover_image_ref : null
      if (typeof previousRef === 'string' && previousRef.trim()) {
        await deleteObject(storageRef(storage, previousRef.trim()))
      }
    } catch (e) {
      // ignore cleanup errors
    }

    const resized = await resizeImageToJpegCover(imageFile)
    const metadata = { contentType: 'image/jpeg' }
    const path = `recipe_book_covers/${recipeBookId}/${Date.now()}-${Math.random().toString(36).slice(2, 9)}.jpg`
    const imageRef = storageRef(storage, path)
    const uploadResult = await uploadBytes(imageRef, resized, metadata)
    const imageUrl = await getDownloadURL(uploadResult.ref)

    await updateRecipeBookFields(recipeBookId, {
      cover_image_ref: path,
      cover_image_url: imageUrl
    })

    return { imageUrl, imageRef: path }
  }

  const generateRecipeBookCover = async (payload: {
    recipeBookId: string
    name: string
    purpose: string
    styleHint?: string
    extraPrompt?: string
    imageModel?: string
  }, background = false): Promise<{ imageUrl?: string; imageRef?: string | null; jobId?: string; status?: 'pending' }> => {
    if (!payload.recipeBookId) throw new Error('generateRecipeBookCover: Missing recipeBookId')
    if (!payload.name.trim()) throw new Error('generateRecipeBookCover: Missing name')
    const functions = getFunctions()

    if (background) {
      const fn = httpsCallable(functions, 'enqueueAIImageJob')
      const result = await fn({
        type: 'generate_recipe_book_cover',
        recipeBookId: payload.recipeBookId,
        name: payload.name,
        purpose: payload.purpose,
        ...(payload.styleHint && payload.styleHint.trim() ? { styleHint: payload.styleHint.trim() } : {}),
        ...(payload.extraPrompt && payload.extraPrompt.trim() ? { extraPrompt: payload.extraPrompt.trim() } : {}),
        ...(payload.imageModel ? { imageModel: payload.imageModel } : {})
      })
      const data = result.data as { jobId: string; status: string }
      return { jobId: data.jobId, status: 'pending' }
    }

    const fn = httpsCallable(functions, 'generateRecipeBookCover')

    const result = await fn({
      recipeBookId: payload.recipeBookId,
      name: payload.name,
      purpose: payload.purpose,
      ...(payload.styleHint && payload.styleHint.trim() ? { styleHint: payload.styleHint.trim() } : {}),
      ...(payload.extraPrompt && payload.extraPrompt.trim() ? { extraPrompt: payload.extraPrompt.trim() } : {}),
      ...(payload.imageModel ? { imageModel: payload.imageModel } : {})
    })

    const data = result.data as { imageUrl?: string; image_ref?: string; error?: string }
    if (!data?.imageUrl || !String(data.imageUrl).trim()) {
      throw new Error(data?.error || 'Failed to generate cover')
    }

    return { imageUrl: data.imageUrl, imageRef: data.image_ref ?? null }
  }

  const addRecipeToRecipeBook = async (
    recipeRef: DocumentReference<Recipe>,
    recipeBookId: string
  ) => {
    const recipeBookRef = doc(firestore, 'recipe_books', recipeBookId)
    const pageRef = doc(collection(recipeBookRef, 'recipe_pages'), recipeRef.id)
    const existingPage = await getDoc(pageRef)

    if (existingPage.exists() && !existingPage.data()?.deleted) return

    await setDoc(pageRef, { ref: recipeRef, deleted: false }, { merge: true })
    await syncRecipeBookReaderUids(recipeBookId, recipeRef.id)
  }

  const addExistingRecipeToRecipeBook = async ({ recipeToAddId, recipeBookToAddTo }: { recipeToAddId: string; recipeBookToAddTo: { id: string } | string }) => {
    const recipeBookId = typeof recipeBookToAddTo === 'string' ? recipeBookToAddTo : recipeBookToAddTo?.id
    if (!recipeToAddId || !recipeBookId) {
      warn('addExistingRecipeToRecipeBook: Missing recipeToAddId or recipeBookId')
      throw new Error('A recipe and target recipe book are required')
    }
    const recipeRef = doc(firestore, 'recipes', recipeToAddId).withConverter(recipeConverter)
    await addRecipeToRecipeBook(recipeRef, recipeBookId)
  }

  const removeRecipeFromRecipeBookInternal = async (recipeId: string, recipeBookId: string) => {
    const recipeBookRef = doc(firestore, 'recipe_books', recipeBookId)
    const pageRef = doc(collection(recipeBookRef, 'recipe_pages'), recipeId)
    await setDoc(pageRef, { deleted: true }, { merge: true })
    await syncRecipeBookReaderUids(recipeBookId, recipeId)
  }

  const removeRecipeFromRecipeBook = async ({ recipeToRemoveId, recipeBookToRemoveFromId }: { recipeToRemoveId: string; recipeBookToRemoveFromId: string }) => {
    if (!recipeToRemoveId || !recipeBookToRemoveFromId) {
      warn('removeRecipeFromRecipeBook: Missing recipeToRemoveId or recipeBookToRemoveFromId')
      return
    }
    await removeRecipeFromRecipeBookInternal(recipeToRemoveId, recipeBookToRemoveFromId)
  }

  const checkIfImageIsUsedByOtherRecipes = async (imageRef: string, excludeRecipeId: string): Promise<boolean> => {
    try {
      // Query all recipes that use this image_ref, excluding the one being deleted
      const recipesQuery = query(
        collection(firestore, 'recipes'),
        where('image_ref', '==', imageRef)
      )

      const recipesSnapshot = await getDocs(recipesQuery)

      // Check if any other recipes (not the one being deleted) use this image
      for (const recipeDoc of recipesSnapshot.docs) {
        if (recipeDoc.id !== excludeRecipeId) {
          return true // Image is still used by another recipe
        }
      }

      return false // Image is not used by any other recipes
    } catch (error) {
      warn('checkIfImageIsUsedByOtherRecipes: Error checking image usage', error)
      // If we can't check, assume it's still used to be safe
      return true
    }
  }

  const deleteRecipeCompletely = async (recipeId: string) => {
    if (!recipeId) {
      warn('deleteRecipeCompletely: Missing recipeId')
      return
    }

    // Get the recipe to check for image
    const recipeRef = doc(firestore, 'recipes', recipeId)
    const recipeSnap = await getDoc(recipeRef)
    const recipeData = recipeSnap.data()

    // Find all recipe books containing this recipe by checking recipe_pages
    const recipeBooksSnapshot = await getDocs(collection(firestore, 'recipe_books'))

    for (const recipeBookDoc of recipeBooksSnapshot.docs) {
      const pageRef = doc(collection(recipeBookDoc.ref, 'recipe_pages'), recipeId)
      const pageSnap = await getDoc(pageRef)

      if (pageSnap.exists() && !pageSnap.data()?.deleted) {
        // Remove from this recipe book
        await setDoc(pageRef, { deleted: true }, { merge: true })
      }
    }

    // Delete the recipe's image from storage if it exists and is not used by other recipes
    if (recipeData?.image_ref) {
      try {
        // Check if this image is used by other recipes before deleting
        const isImageStillUsed = await checkIfImageIsUsedByOtherRecipes(recipeData.image_ref, recipeId)

        if (!isImageStillUsed) {
          const imageRef = storageRef(storage, recipeData.image_ref)
          await deleteObject(imageRef)
        } else {
          debug('deleteRecipeCompletely: Image still used by other recipes, keeping it')
        }
      } catch (error) {
        // Image may not exist or deletion failed, continue with recipe deletion
        warn('deleteRecipeCompletely: Failed to delete image', error)
      }
    }

    // Delete the actual recipe document
    await deleteDoc(recipeRef)
  }

  const attachImageToRecipe = async (imageFile: File, recipeId: string) => {
    const resized = await resizeImageToJpegCover(imageFile)
    const metadata = {
      contentType: 'image/jpeg'
    }
    const path = `recipe_images/${recipeId}.jpg`
    const imageRef = storageRef(storage, path)
    const uploadResult = await uploadBytes(imageRef, resized, metadata)
    const imageUrl = await getDownloadURL(uploadResult.ref)
    await updateDoc(doc(firestore, 'recipes', recipeId), {
      image_ref: path,
      image_url: imageUrl
    })
  }

  const generateRecipeImage = async (payload: {
    recipeId?: string
    recipeName: string
    description?: string
    ingredients?: Array<{ name?: string }>
    styleHint?: string
    extraPrompt?: string
    imageModel?: string
  }, background = false): Promise<{ imageUrl?: string; imageRef?: string | null; jobId?: string; status?: 'pending' }> => {
    const functions = getFunctions()

    if (background) {
      const fn = httpsCallable(functions, 'enqueueAIImageJob')
      const result = await fn({
        type: 'generate_recipe_image',
        recipeId: payload.recipeId,
        recipeName: payload.recipeName,
        description: payload.description,
        ingredients: payload.ingredients ?? [],
        ...(payload.styleHint && payload.styleHint.trim() ? { styleHint: payload.styleHint.trim() } : {}),
        ...(payload.extraPrompt && payload.extraPrompt.trim() ? { extraPrompt: payload.extraPrompt.trim() } : {}),
        ...(payload.imageModel ? { imageModel: payload.imageModel } : {})
      })
      const data = result.data as { jobId: string; status: string }
      return { jobId: data.jobId, status: 'pending' }
    }

    const fn = httpsCallable(functions, 'generateRecipeImage')

    const result = await fn({
      recipeName: payload.recipeName,
      description: payload.description,
      ingredients: payload.ingredients ?? [],
      ...(payload.styleHint && payload.styleHint.trim() ? { styleHint: payload.styleHint.trim() } : {}),
      ...(payload.extraPrompt && payload.extraPrompt.trim() ? { extraPrompt: payload.extraPrompt.trim() } : {}),
      ...(payload.imageModel ? { imageModel: payload.imageModel } : {})
    })

    const data = result.data as { imageUrl?: string; image_ref?: string; error?: string }
    if (!data?.imageUrl || !String(data.imageUrl).trim()) {
      throw new Error(data?.error || 'Failed to generate image')
    }

    return { imageUrl: data.imageUrl, imageRef: data.image_ref ?? null }
  }

  const generateStepImage = async (payload: {
    recipeId?: string
    stepText: string
    ingredients: Array<{ name?: string }>
    stepNumber: number
    styleHint?: string
    extraPrompt?: string
    imageModel?: string
  }, background = false): Promise<{ imageUrl?: string; imageRef?: string | null; jobId?: string; status?: 'pending' }> => {
    const functions = getFunctions()

    if (background) {
      const fn = httpsCallable(functions, 'enqueueAIImageJob')
      const result = await fn({
        type: 'generate_step_image',
        recipeId: payload.recipeId,
        stepText: payload.stepText,
        ingredients: payload.ingredients,
        stepNumber: payload.stepNumber,
        ...(payload.styleHint && payload.styleHint.trim() ? { styleHint: payload.styleHint.trim() } : {}),
        ...(payload.extraPrompt && payload.extraPrompt.trim() ? { extraPrompt: payload.extraPrompt.trim() } : {}),
        ...(payload.imageModel ? { imageModel: payload.imageModel } : {})
      })
      const data = result.data as { jobId: string; status: string }
      return { jobId: data.jobId, status: 'pending' }
    }

    const fn = httpsCallable(functions, 'generateStepImage')

    const result = await fn({
      stepText: payload.stepText,
      ingredients: payload.ingredients,
      stepNumber: payload.stepNumber,
      ...(payload.styleHint && payload.styleHint.trim() ? { styleHint: payload.styleHint.trim() } : {}),
      ...(payload.extraPrompt && payload.extraPrompt.trim() ? { extraPrompt: payload.extraPrompt.trim() } : {}),
      ...(payload.imageModel ? { imageModel: payload.imageModel } : {})
    })

    const data = result.data as { imageUrl?: string; imageRef?: string; error?: string }
    if (!data?.imageUrl || !String(data.imageUrl).trim()) {
      throw new Error(data?.error || 'Failed to generate step image')
    }

    return { imageUrl: data.imageUrl, imageRef: data.imageRef ?? null }
  }

  const enhanceImage = async (payload: {
    recipeId?: string
    recipeBookId?: string
    imageUrl: string
    imageType: 'recipe' | 'step' | 'recipe_book_cover'
    context?: {
      recipeName?: string
      description?: string
      ingredients?: Array<{ name?: string }>
      stepText?: string
      stepNumber?: number
    }
    extraPrompt?: string
    imageModel?: string
  }, background = false): Promise<{ imageUrl?: string; imageRef?: string | null; jobId?: string; status?: 'pending' }> => {
    if (!payload.imageUrl || !payload.imageUrl.trim()) {
      throw new Error('enhanceImage: Missing imageUrl')
    }

    const functions = getFunctions()

    if (background) {
      const fn = httpsCallable(functions, 'enqueueAIImageJob')
      const result = await fn({
        type: 'enhance_image',
        recipeId: payload.recipeId,
        recipeBookId: payload.recipeBookId,
        imageUrl: payload.imageUrl,
        imageType: payload.imageType,
        context: payload.context ?? {},
        ...(payload.extraPrompt && payload.extraPrompt.trim() ? { extraPrompt: payload.extraPrompt.trim() } : {}),
        ...(payload.imageModel ? { imageModel: payload.imageModel } : {})
      })
      const data = result.data as { jobId: string; status: string }
      return { jobId: data.jobId, status: 'pending' }
    }

    const fn = httpsCallable(functions, 'enhanceImage')

    const result = await fn({
      recipeId: payload.recipeId,
      recipeBookId: payload.recipeBookId,
      imageUrl: payload.imageUrl,
      imageType: payload.imageType,
      context: payload.context ?? {},
      ...(payload.extraPrompt && payload.extraPrompt.trim() ? { extraPrompt: payload.extraPrompt.trim() } : {}),
      ...(payload.imageModel ? { imageModel: payload.imageModel } : {})
    })

    const data = result.data as { imageUrl?: string; imageRef?: string; error?: string }
    if (!data?.imageUrl || !String(data.imageUrl).trim()) {
      throw new Error(data?.error || 'Failed to enhance image')
    }

    return { imageUrl: data.imageUrl, imageRef: data.imageRef ?? null }
  }

  // Async enqueue variants - these reserve credits and return a job id which will be processed in background
  const enqueueRecipeImageJob = async (payload: {
    recipeId?: string
    recipeName?: string
    description?: string
    ingredients?: Array<{ name?: string }>
    styleHint?: string
    extraPrompt?: string
    imageModel?: string
  }) => {
    const isLocal = typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname.startsWith('127.'))
    const base = 'https://us-central1-shelf-backend.cloudfunctions.net'

    if (isLocal) {
      try {
        // Try HTTP POST fallback with idToken auth
        const { getAuth } = await import('firebase/auth')
        const auth = getAuth()
        const currentUser = auth.currentUser
        const headers: Record<string, string> = { 'Content-Type': 'application/json' }
        if (currentUser) {
          const idToken = await currentUser.getIdToken()
          headers['Authorization'] = `Bearer ${idToken}`
        }

        const res = await fetch(`${base}/enqueueAIImageJobHttp`, {
          method: 'POST',
          headers,
          body: JSON.stringify({
            type: 'generate_recipe_image',
            recipeId: payload.recipeId,
            recipeName: payload.recipeName,
            description: payload.description,
            ingredients: payload.ingredients ?? [],
            styleHint: payload.styleHint,
            extraPrompt: payload.extraPrompt,
            imageModel: payload.imageModel
          })
        })

        const data = await res.json()
        if (!res.ok) {
          const err = new Error(data?.message || 'Failed to enqueue job')
            ; (err as any).code = data?.error
          throw err
        }

        return { jobId: data.jobId, status: data.status }
      } catch (err) {
        console.warn('enqueueRecipeImageJob (http) failed, falling back to callable', err)
      }
    }

    const functions = getFunctions()
    const fn = httpsCallable(functions, 'enqueueAIImageJob')

    const result = await fn({
      type: 'generate_recipe_image',
      recipeId: payload.recipeId,
      recipeName: payload.recipeName,
      description: payload.description,
      ingredients: payload.ingredients ?? [],
      styleHint: payload.styleHint,
      extraPrompt: payload.extraPrompt,
      imageModel: payload.imageModel
    })

    return result.data as { jobId: string; status: string }
  }

  const enqueueStepImageJob = async (payload: {
    recipeId?: string
    stepNumber: number
    stepText?: string
    ingredients?: Array<{ name?: string }>
    styleHint?: string
    extraPrompt?: string
    imageModel?: string
  }) => {
    const functions = getFunctions()
    const fn = httpsCallable(functions, 'enqueueAIImageJob')

    const result = await fn({
      type: 'generate_step_image',
      recipeId: payload.recipeId,
      stepNumber: payload.stepNumber,
      stepText: payload.stepText,
      ingredients: payload.ingredients ?? [],
      styleHint: payload.styleHint,
      extraPrompt: payload.extraPrompt,
      imageModel: payload.imageModel
    })

    return result.data as { jobId: string; status: string }
  }

  const enqueueEnhanceImageJob = async (payload: {
    imageUrl: string
    imageType: 'recipe' | 'step'
    context?: Record<string, unknown>
    extraPrompt?: string
    imageModel?: string
  }) => {
    const isLocal = typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname.startsWith('127.'))
    const base = 'https://us-central1-shelf-backend.cloudfunctions.net'

    if (isLocal) {
      try {
        const { getAuth } = await import('firebase/auth')
        const auth = getAuth()
        const currentUser = auth.currentUser
        const headers: Record<string, string> = { 'Content-Type': 'application/json' }
        if (currentUser) {
          const idToken = await currentUser.getIdToken()
          headers['Authorization'] = `Bearer ${idToken}`
        }

        const res = await fetch(`${base}/enqueueAIImageJobHttp`, {
          method: 'POST',
          headers,
          body: JSON.stringify({
            type: 'enhance_image',
            imageUrl: payload.imageUrl,
            imageType: payload.imageType,
            context: payload.context ?? {},
            extraPrompt: payload.extraPrompt,
            imageModel: payload.imageModel
          })
        })

        const data = await res.json()
        if (!res.ok) {
          const err = new Error(data?.message || 'Failed to enqueue job')
            ; (err as any).code = data?.error
          throw err
        }

        return { jobId: data.jobId, status: data.status }
      } catch (err) {
        console.warn('enqueueEnhanceImageJob (http) failed, falling back to callable', err)
      }
    }

    const functions = getFunctions()
    const fn = httpsCallable(functions, 'enqueueAIImageJob')

    const result = await fn({
      type: 'enhance_image',
      imageUrl: payload.imageUrl,
      imageType: payload.imageType,
      context: payload.context ?? {},
      extraPrompt: payload.extraPrompt,
      imageModel: payload.imageModel
    })

    return result.data as { jobId: string; status: string }
  }

  const getAiImageCosts = async (): Promise<{ costs: Record<string, number>; userCredits?: { total: number; remaining: number; rolloverCap: number } }> => {
    // Prefer HTTP endpoint in local dev to avoid callable/CORS issues
    const isLocal = typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname.startsWith('127.'))

    const base = 'https://us-central1-shelf-backend.cloudfunctions.net'

    if (isLocal) {
      try {
        // Include ID token if available for user-specific credits
        const { getAuth } = await import('firebase/auth')
        const auth = getAuth()
        const currentUser = auth.currentUser
        const headers: Record<string, string> = { 'Content-Type': 'application/json' }
        if (currentUser) {
          const idToken = await currentUser.getIdToken()
          headers['Authorization'] = `Bearer ${idToken}`
        }

        const res = await fetch(`${base}/getAiImageCostsHttp`, { method: 'GET', headers })
        if (!res.ok) throw new Error('http error')
        const data = await res.json()
        return { costs: data.costs, userCredits: data.userCredits }
      } catch (err) {
        // Fall through to callable attempt below if HTTP fails
        console.warn('getAiImageCosts (http) failed, falling back to callable', err)
      }
    }

    // Default: try callable function (works on deployed site)
    const functions = getFunctions()
    const fn = httpsCallable(functions, 'getAiImageCosts')
    const resp = await fn({})
    return resp.data as { costs: Record<string, number>; userCredits?: { total: number; remaining: number; rolloverCap: number } }
  }

  const saveRecipe = async (payload: SaveRecipePayload) => {
    const recipesRef = collection(firestore, 'recipes').withConverter(recipeConverter)
    const recipeRef: DocumentReference<Recipe> = payload.recipeId ? doc(recipesRef, payload.recipeId) : doc(recipesRef)

    const recipeData: Record<string, unknown> = {
      name: payload.name,
      discoverable: payload.discoverable,
      searchableName: payload.name.toLowerCase().split(' '),
      owner: payload.ownerId ?? user.value?.uid ?? undefined,
      directions: payload.directions,
      tags: payload.tags,
      updated_at: serverTimestamp()
    }

    if (payload.description !== undefined) {
      recipeData.description = payload.description
    }

    // Only include ingredients if explicitly provided (for legacy recipes)
    // New step-based recipes have ingredients inside directions
    if (payload.ingredients !== undefined) {
      recipeData.ingredients = payload.ingredients
    }

    // Recipe metadata
    if (payload.servings !== undefined) {
      recipeData.servings = payload.servings
    }
    if (payload.prepTime) {
      recipeData.prep_time = payload.prepTime
    }
    if (payload.cookTime) {
      recipeData.cook_time = payload.cookTime
    }
    if (payload.difficulty !== undefined) {
      recipeData.difficulty = payload.difficulty
    }

    // Add created_at only for new recipes
    if (!payload.recipeId) {
      recipeData.created_at = serverTimestamp()
    }

    await setDoc(recipeRef, recipeData, { merge: true })

    if (payload.imageFile) {
      await attachImageToRecipe(payload.imageFile, recipeRef.id)
    } else if (payload.imageUrl) {
      // For AI-generated images, store the URL and optional storage ref directly
      const docUpdates: Record<string, unknown> = { image_url: payload.imageUrl }
      if (payload.imageRef) {
        docUpdates.image_ref = payload.imageRef
      }
      await setDoc(recipeRef, docUpdates, { merge: true })
    }

    const addedBooks = payload.recipeBooks.filter(
      (rb) => !payload.previousRecipeBooks.some((prev) => prev.id === rb.id)
    )
    for (const book of addedBooks) {
      await addRecipeToRecipeBook(recipeRef, book.id)
    }

    const removedBooks = payload.previousRecipeBooks.filter(
      (prev) => !payload.recipeBooks.some((book) => book.id === prev.id)
    )
    for (const book of removedBooks) {
      await removeRecipeFromRecipeBookInternal(recipeRef.id, book.id)
    }

    return recipeRef.id
  }

  const generateRecipeDescription = async (payload: {
    recipeName: string
    ingredients: Array<{ name: string }>
    steps?: string[]
  }): Promise<string> => {
    const functions = getFunctions()
    const fn = httpsCallable(functions, 'generateRecipeDescription')

    const result = await fn({
      recipeName: payload.recipeName,
      ingredients: payload.ingredients,
      steps: payload.steps ?? []
    })

    const data = result.data as { description?: string; error?: string }
    if (typeof data.description === 'string' && data.description.trim()) {
      return data.description
    }
    throw new Error(data.error || 'Failed to generate description')
  }

  const cloneRecipe = async (originalRecipeId: string, targetRecipeBookId: string): Promise<string | null> => {
    if (!originalRecipeId || !targetRecipeBookId) {
      warn('cloneRecipe: Missing originalRecipeId or targetRecipeBookId')
      return null
    }

    if (!user.value?.uid) {
      warn('cloneRecipe: No user logged in')
      return null
    }

    // Fetch the original recipe
    const originalRef = doc(firestore, 'recipes', originalRecipeId).withConverter(recipeConverter)
    const originalSnap = await getDoc(originalRef)

    if (!originalSnap.exists()) {
      warn('cloneRecipe: Original recipe not found')
      return null
    }

    const original = originalSnap.data()

    // Create the clone with new owner and clone reference
    const recipesRef = collection(firestore, 'recipes').withConverter(recipeConverter)
    const cloneRef = doc(recipesRef)

    const cloneData: Record<string, unknown> = {
      name: original.name,
      discoverable: false, // Clones start as private
      searchableName: original.name.toLowerCase().split(' '),
      owner: user.value.uid,
      ingredients: original.ingredients ? [...original.ingredients] : [],
      directions: original.directions ? [...original.directions] : [],
      tags: original.tags ? [...original.tags] : [],
      cloned_from: originalRecipeId,
      cloned_at: serverTimestamp(),
      created_at: serverTimestamp(),
    }

    // Copy the image if the original has one
    if (original.image_ref) {
      try {
        const originalImageRef = storageRef(storage, original.image_ref)
        const imageBytes = await getBytes(originalImageRef)

        // Get the file extension from the original path
        const ext = original.image_ref.slice(original.image_ref.lastIndexOf('.'))
        const newPath = `recipe_images/${user.value.uid}/${cloneRef.id}${ext}`
        const newImageRef = storageRef(storage, newPath)

        // Upload the copied image
        const metadata = { contentType: `image/${ext.slice(1)}` }
        await uploadBytes(newImageRef, imageBytes, metadata)
        const newImageUrl = await getDownloadURL(newImageRef)

        cloneData.image_ref = newPath
        cloneData.image_url = newImageUrl
      } catch (error) {
        // If image copy fails, continue without image
        warn('cloneRecipe: Failed to copy image', error)
        cloneData.image_ref = null
        cloneData.image_url = null
      }
    }

    await setDoc(cloneRef, cloneData)

    // Add to the target recipe book
    await addRecipeToRecipeBook(cloneRef, targetRecipeBookId)

    return cloneRef.id
  }

  const getOriginalRecipe = async (clonedFromId: string): Promise<Recipe | null> => {
    if (!clonedFromId) return null

    const originalRef = doc(firestore, 'recipes', clonedFromId).withConverter(recipeConverter)
    const originalSnap = await getDoc(originalRef)

    if (!originalSnap.exists()) return null

    return { ...originalSnap.data(), id: originalSnap.id }
  }

  const getUserDisplayName = async (userId: string): Promise<string | null> => {
    if (!userId) return null

    const userRef = doc(firestore, 'public_profiles', userId)
    const userSnap = await getDoc(userRef)

    if (!userSnap.exists()) return null

    const userData = userSnap.data()
    return userData?.name || userData?.displayName || null
  }

  const updateRecipeFields = async (recipeId: string, updates: Partial<Recipe>) => {
    if (!recipeId) {
      warn('updateRecipeFields: Missing recipeId')
      return
    }

    const recipeRef = doc(firestore, 'recipes', recipeId)

    // Convert cloned_at to serverTimestamp if it's being updated
    const firestoreUpdates: Record<string, unknown> = { ...updates }
    if ('cloned_at' in updates) {
      firestoreUpdates.cloned_at = serverTimestamp()
    }

    await updateDoc(recipeRef, firestoreUpdates)
  }

  const unbindAllRecipeData = () => {
    unbindCurrentRecipe()
    unbindAllSubcollections()
    searchQuery.value = null
    currentRecipe.value = null
    clearDiscoveredRecipes()
  }

  const updateRecipeImage = async (recipeId: string, imageUrl: string, imageRef?: string) => {
    if (!recipeId) return
    const recipeRef = doc(firestore, 'recipes', recipeId)
    await updateDoc(recipeRef, {
      image_url: imageUrl,
      image_ref: imageRef || null
    })
  }

  const updateRecipeStepImage = async (recipeId: string, stepNumber: number, imageUrl: string, imageRef?: string) => {
    if (!recipeId || !Number.isInteger(stepNumber) || stepNumber < 1 || !imageUrl) return

    const recipeRef = doc(firestore, 'recipes', recipeId)
    const snap = await getDoc(recipeRef)
    if (!snap.exists()) return

    const data = snap.data() as DocumentData
    const directionsArr = Array.isArray(data?.directions) ? [...data.directions] : []
    const idx = stepNumber - 1
    if (!directionsArr[idx]) return

    const updatedStep = { ...(directionsArr[idx] as Record<string, unknown>) }
    updatedStep.imageUrl = imageUrl
    updatedStep.imageRef = imageRef || null
    directionsArr[idx] = updatedStep

    await updateDoc(recipeRef, { directions: directionsArr })
  }

  const scanRecipeImages = async (images: string[], visionModel?: string): Promise<ScannedRecipe> => {
    if (images.length === 0 || images.length > 5) {
      throw new Error('Select between 1 and 5 recipe images.')
    }
    const scanFn = httpsCallable<
      { images: string[]; visionModel?: string },
      { recipe: ScannedRecipe }
    >(getFunctions(), 'scanRecipeImage')
    const result = await scanFn({ images, ...(visionModel ? { visionModel } : {}) })
    return result.data.recipe
  }

  const enqueueRecipeScan = async (files: File[], visionModel?: string): Promise<void> => {
    const uid = userStore.firebaseUser?.uid
    if (!uid) throw new Error('Sign in to scan recipe images.')
    if (files.length === 0 || files.length > 5) {
      throw new Error('Select between 1 and 5 recipe images.')
    }
    const imageRefs: string[] = []
    const uploadId = crypto.randomUUID()
    for (const [index, file] of files.entries()) {
      const path = `temp_uploads/${uid}/${uploadId}_${index}_${file.name}`
      await uploadBytes(storageRef(storage, path), file, { contentType: file.type })
      imageRefs.push(path)
    }
    await useAiJobStore().enqueueJob('scan_recipe', {
      imageRefs,
      ...(visionModel ? { visionModel } : {})
    })
  }

  return {
    // State
    recipeBooks,
    recipeBooksArray,
    searchQuery,
    currentRecipe,
    discoveredRecipes,
    lastDiscoveredRecipe,
    moreToDiscover,

    // VueFire state
    recipeBooksLoading,
    recipeBooksError,
    unfiledRecipes,

    // Getters
    getRecipeBooks,
    getRecipeBooksDocs,
    getSearchQuery,
    getCurrentRecipe,
    getDiscoveredRecipes,
    discoveredRecipe: getDiscoveredRecipes,
    getLastDiscoveredRecipe,
    getMoreToDiscover,
    recipeFromRecipeBook,
    recipeBooksContainingRecipe,
    recipeBook,
    getRecipeBookMembers: recipeBookMembers,
    recipeBookMembers,
    recipeBookRecipesFiltered,
    userAddedShoppingListItems,
    loading,
    getIngredientsSmartSuggestions,
    getAllRecipes,
    getRecipesByIds,

    // Actions
    setSearchQuery,
    setCurrentRecipe,
    clearDiscoveredRecipes,
    addDiscoveredRecipe,
    setLastDiscoveredRecipe,
    setMoreToDiscover,
    bindCurrentRecipe,
    bindRecipeBookMembers,
    bindRecipeBookRecipes,
    bindAllRecipeBookRecipes,
    addUserToRecipeBook,
    removeUserFromRecipeBook,
    discoverMoreRecipes,
    fetchPublicDiscoverableRecipes,
    saveRecipe,
    scanRecipeImages,
    enqueueRecipeScan,
    generateRecipeDescription,
    generateRecipeImage,
    generateStepImage,
    enhanceImage,
    getAiImageCosts,
    // Async enqueue methods
    enqueueRecipeImageJob,
    enqueueStepImageJob,
    enqueueEnhanceImageJob,
    unbindAllRecipeData,
    upsertRecipeBook,
    upsertRecipeBookWithDetails,
    ensurePersonalRecipeBook,
    updateRecipeBookFields,
    applyRecipeBookCoverFromJob,
    attachCoverImageToRecipeBook,
    generateRecipeBookCover,
    addRecipeToRecipeBook,
    addExistingRecipeToRecipeBook,
    removeRecipeFromRecipeBook,
    deleteRecipeCompletely,
    cloneRecipe,
    getOriginalRecipe,
    getUserDisplayName,
    updateRecipeFields,
    updateRecipeImage,
    updateRecipeStepImage,
    testFirebaseConnection: async () => { console.log('Firebase connected') }
  }
})
